"use client"

import React, { useState, useEffect, useRef, useCallback } from "react"
import { useFormContext } from "react-hook-form"
import { Input } from "@/shared/components/ui/input"
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/shared/components/ui/popover"
import FormTextInputWrapper, { InputGroupProps } from "@/shared/components/form-inputs/form-input-wrapper"
import { getVolunteerTableRows } from "../actions/volunteers.actions"
import type { NonHomeownerUser } from "../types/volunteers.type"
import type { VolunteerFormType } from "../schemas/volunteers.schema"
import type { CreateVolunteersFormType } from "../schemas/volunteers.schema"
import { NO_EMAIL_AVAILABLE_OPTION, getDisplayEmailValue } from "../schemas/volunteers.schema"
import {
    mergeServePeriodsForGroupAddForm,
    type DefaultServePeriod,
} from "../lib/merge-serve-periods-for-form"

export type { DefaultServePeriod }

const DEBOUNCE_MS = 300
const SEARCH_LIMIT = 20

function displayLabel(user: NonHomeownerUser): string {
    const name = user.fullname?.trim() || "Unknown"
    const email = user.email?.trim() || ""
    return email ? `${name} (${email})` : name
}

function getVolunteerOrganizationName(user: NonHomeownerUser): string {
    return user.volunteer?.organizationName ?? ""
}

function getVolunteerPreferredLocation(user: NonHomeownerUser): string {
    return user.volunteer?.preferredLocation ?? ""
}

function setOrganizationFromUserOrKeepDefault(
    setValue: (name: any, value: any, opts?: { shouldDirty?: boolean }) => void,
    organizationPath: string,
    user: NonHomeownerUser,
) {
    const existing = getVolunteerOrganizationName(user).trim()
    if (existing) {
        setValue(organizationPath as any, existing, { shouldDirty: true })
    }
}

function restoreGroupOrganizationField(
    setValue: (name: any, value: any, opts?: { shouldDirty?: boolean }) => void,
    organizationPath: string | undefined,
    defaultGroupOrganization?: string,
) {
    if (!organizationPath) return
    const d = defaultGroupOrganization?.trim() ?? ""
    setValue(organizationPath as any, d, { shouldDirty: true })
}

type VolunteerSearchComboboxProps = {
    /** Form path for full name (e.g. "fullName" or "volunteers.0.fullName") */
    fullNamePath: string
    /** Form path for email (e.g. "email" or "volunteers.0.email") */
    emailPath: string
    /** Optional: also set these paths when selecting a user (autofill) */
    optionalPaths?: {
        phone?: string
        organizationName?: string
        preferredLocation?: string
        dateOfBirth?: string
        /** Path to servePeriods array (e.g. "volunteers.0.servePeriods"); value is set from user.volunteer?.serveDates */
        servePeriods?: string
        /** Existing user's profile image URL for preview (e.g. "volunteers.0.profilePicture") */
        profilePicture?: string
    }
    /** When in a group context, append this period to the user's existing serve dates on auto-fill */
    defaultServePeriod?: DefaultServePeriod
    /** Add-from-group only: prefilled org; cleared name/email restores this; select keeps it if user has no org */
    defaultGroupOrganization?: string
    inputGroupProps?: InputGroupProps
    placeholder?: string
    disabled?: boolean
    /** When true, required-field highlight applies when fullName or email is empty */
    highlightWhenEmpty?: boolean
    /** Label for the combobox (e.g. "Full Name *") */
    label?: string
}

export function VolunteerSearchCombobox({
    fullNamePath,
    emailPath,
    optionalPaths,
    defaultServePeriod,
    defaultGroupOrganization,
    inputGroupProps,
    placeholder = "Enter or Search by name",
    disabled = false,
    highlightWhenEmpty = true,
    label = "Full Name *",
}: VolunteerSearchComboboxProps) {
    const { setValue, watch, formState: { errors } } = useFormContext<VolunteerFormType | CreateVolunteersFormType>()
    const fullName = watch(fullNamePath as any) as unknown as string | undefined
    const email = watch(emailPath as any) as unknown as string | undefined

    const [searchQuery, setSearchQuery] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const [options, setOptions] = useState<NonHomeownerUser[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const listScrollRef = useRef<HTMLDivElement>(null)

    const displayValue = fullName || ""

    const effectiveValue = isOpen ? searchQuery : displayValue
    const isEmpty = !(fullName?.trim() || email?.trim())
    const showHighlight = highlightWhenEmpty && isEmpty

    const runSearch = useCallback(async (query: string) => {
        const term = query?.trim()
        if (!term) {
            setOptions([])
            return
        }
        setIsSearching(true)
        try {
            const result = await getVolunteerTableRows({
                search: term,
                page: 1,
                limit: SEARCH_LIMIT,
            })
            if (result.success && result.data?.users) {
                setOptions(result.data.users as NonHomeownerUser[])
            } else {
                setOptions([])
            }
        } catch {
            setOptions([])
        } finally {
            setIsSearching(false)
        }
    }, [])

    useEffect(() => {
        if (!isOpen) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            runSearch(searchQuery)
            debounceRef.current = null
        }, DEBOUNCE_MS)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchQuery, isOpen, runSearch])

    // Let touch scroll work inside the dropdown when it's inside a dialog (dialog scroll-lock would otherwise prevent touchmove)
    useEffect(() => {
        if (!isOpen) return
        let remove: (() => void) | null = null
        const id = requestAnimationFrame(() => {
            if (!listScrollRef.current) return
            const el = listScrollRef.current
            const handleTouchMove = (e: TouchEvent) => {
                if (el.contains(e.target as Node)) e.stopImmediatePropagation()
            }
            document.addEventListener('touchmove', handleTouchMove, { capture: true })
            remove = () => document.removeEventListener('touchmove', handleTouchMove, { capture: true })
        })
        return () => {
            cancelAnimationFrame(id)
            remove?.()
        }
    }, [isOpen])

    const handleSelect = (user: NonHomeownerUser) => {
        setValue(fullNamePath as any, user.fullname ?? "", { shouldValidate: true, shouldDirty: true })
        setValue(emailPath as any, user.email ?? "", { shouldValidate: true, shouldDirty: true })
        if (optionalPaths?.phone) setValue(optionalPaths.phone as any, user.phone ?? "", { shouldDirty: true })
        if (optionalPaths?.organizationName) {
            setOrganizationFromUserOrKeepDefault(setValue, optionalPaths.organizationName, user)
        }
        if (optionalPaths?.preferredLocation) setValue(optionalPaths.preferredLocation as any, getVolunteerPreferredLocation(user), { shouldDirty: true })
        if (optionalPaths?.dateOfBirth && user.dateOfBirth != null) {
            const dob = typeof user.dateOfBirth === "string" ? user.dateOfBirth : user.dateOfBirth
            setValue(optionalPaths.dateOfBirth as any, dob ?? "", { shouldDirty: true })
        }
        if (optionalPaths?.servePeriods) {
            const periods = mergeServePeriodsForGroupAddForm(
                user.volunteer?.serveDates,
                defaultServePeriod,
            )
            if (periods.length > 0) {
                setValue(optionalPaths.servePeriods as any, periods, { shouldDirty: true })
            }
        }
        if (optionalPaths?.profilePicture) {
            const url = (user as { profilePicture?: string | null }).profilePicture
            setValue(optionalPaths.profilePicture as any, url ?? undefined, { shouldDirty: true })
        }
        setSearchQuery("")
        setIsOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setValue(fullNamePath as any, "", { shouldValidate: true, shouldDirty: true })
        setValue(emailPath as any, "", { shouldValidate: true, shouldDirty: true })
        restoreGroupOrganizationField(setValue, optionalPaths?.organizationName, defaultGroupOrganization)
        if (optionalPaths?.profilePicture) {
            setValue(optionalPaths.profilePicture as any, undefined, { shouldDirty: true })
        }
        setSearchQuery("")
        setIsOpen(false)
    }

    const handleInputChange = (value: string) => {
        setSearchQuery(value)
        if (!isOpen) setIsOpen(true)
        if (!value.trim()) {
            setValue(fullNamePath as any, "", { shouldValidate: true, shouldDirty: true })
            setValue(emailPath as any, "", { shouldValidate: true, shouldDirty: true })
            restoreGroupOrganizationField(setValue, optionalPaths?.organizationName, defaultGroupOrganization)
            if (optionalPaths?.profilePicture) {
                setValue(optionalPaths.profilePicture as any, undefined, { shouldDirty: true })
            }
        } else {
            setValue(fullNamePath as any, value, { shouldValidate: true, shouldDirty: true })
        }
    }

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open)
        if (!open) setSearchQuery("")
    }

    function getErrorByPath(path: string): string | undefined {
        const parts = path.split(".")
        let obj: unknown = errors
        for (const part of parts) {
            if (obj == null) return undefined
            obj = (obj as Record<string, unknown>)[part]
        }
        return obj && typeof obj === "object" && "message" in obj && typeof (obj as { message?: unknown }).message === "string"
            ? (obj as { message: string }).message
            : undefined
    }
    const fullNameError = getErrorByPath(fullNamePath)
    const errorMessage = fullNameError || inputGroupProps?.error

    return (
        <FormTextInputWrapper
            reserveMessageSlot
            inputGroupProps={{
                label,
                ...inputGroupProps,
                error: errorMessage,
                isDirty: !!fullName || !!email,
                highlightWhenEmpty: inputGroupProps?.highlightWhenEmpty ?? highlightWhenEmpty,
            }}
        >
            <div ref={containerRef} className="relative w-full">
                <Popover open={isOpen} onOpenChange={handleOpenChange}>
                    <PopoverTrigger asChild>
                        <div className="relative">
                            <Input
                                type="text"
                                placeholder={placeholder}
                                value={effectiveValue}
                                onChange={(e) => handleInputChange((e.target as HTMLInputElement).value)}
                                disabled={disabled}
                                variant={errorMessage ? "destructive" : "default"}
                                className={cn(
                                    "w-full pr-16",
                                    errorMessage && "border-destructive",
                                    showHighlight && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20"
                                )}
                                style={showHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : undefined}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                {displayValue && (
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        className="p-0.5 rounded hover:bg-muted"
                                        aria-label="Clear selection"
                                    >
                                        <X className="h-4 w-4 text-muted-foreground" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="p-0.5 rounded hover:bg-muted"
                                    onClick={() => setIsOpen(!isOpen)}
                                    disabled={disabled}
                                    aria-label="Toggle list"
                                >
                                    <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                                </button>
                            </div>
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[var(--radix-popover-trigger-width)] p-0"
                        align="start"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                        <div
                            ref={listScrollRef}
                            className="max-h-[280px] overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y"
                            style={{ WebkitOverflowScrolling: 'touch' }}
                            onWheel={(e) => e.stopPropagation()}
                        >
                            {isSearching ? (
                                <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Searching...
                                </div>
                            ) : options.length === 0 && searchQuery.trim() ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                    No volunteers found. Type name and email below to add new.
                                </div>
                            ) : options.length > 0 ? (
                                <div className="p-1">
                                    {options.map((user) => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => handleSelect(user)}
                                            className={cn(
                                                "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                                                "hover:bg-accent hover:text-accent-foreground",
                                                fullName === user.fullname && email === user.email && "bg-accent"
                                            )}
                                        >
                                            <Check
                                                className={cn(
                                                    "h-4 w-4 shrink-0",
                                                    fullName === user.fullname && email === user.email ? "opacity-100 text-primary" : "opacity-0"
                                                )}
                                            />
                                            <span className="truncate">{user.fullname?.trim() || "Unknown"}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="px-3 py-3 text-center text-xs text-muted-foreground leading-snug">
                                    Type to search existing, or enter new.
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </FormTextInputWrapper>
    )
}

// --- Email combobox (search by email, same API; on select sets fullName + email + optionalPaths) ---

type VolunteerEmailComboboxProps = {
    fullNamePath: string
    emailPath: string
    optionalPaths?: VolunteerSearchComboboxProps["optionalPaths"]
    defaultServePeriod?: DefaultServePeriod
    defaultGroupOrganization?: string
    inputGroupProps?: InputGroupProps
    placeholder?: string
    disabled?: boolean
    highlightWhenEmpty?: boolean
    label?: string
}

export function VolunteerEmailCombobox({
    fullNamePath,
    emailPath,
    optionalPaths,
    defaultServePeriod,
    defaultGroupOrganization,
    inputGroupProps,
    placeholder = "Enter or Search by email",
    disabled = false,
    highlightWhenEmpty = true,
    label = "Email *",
}: VolunteerEmailComboboxProps) {
    const { setValue, watch, formState: { errors } } = useFormContext<VolunteerFormType | CreateVolunteersFormType>()
    const fullName = watch(fullNamePath as any) as unknown as string | undefined
    const email = watch(emailPath as any) as unknown as string | undefined

    const [searchQuery, setSearchQuery] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const [options, setOptions] = useState<NonHomeownerUser[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const listScrollRef = useRef<HTMLDivElement>(null)

    const displayValue = email ?? ""
    const effectiveValue = isOpen ? searchQuery : displayValue
    const isEmpty = !(fullName?.trim() || email?.trim())
    const showHighlight = highlightWhenEmpty && isEmpty

    const runSearch = useCallback(async (query: string) => {
        const term = query?.trim()
        if (!term) {
            setOptions([])
            return
        }
        setIsSearching(true)
        try {
            const result = await getVolunteerTableRows({
                search: term,
                page: 1,
                limit: SEARCH_LIMIT,
            })
            if (result.success && result.data?.users) {
                setOptions(result.data.users as NonHomeownerUser[])
            } else {
                setOptions([])
            }
        } catch {
            setOptions([])
        } finally {
            setIsSearching(false)
        }
    }, [])

    useEffect(() => {
        if (!isOpen) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            runSearch(searchQuery)
            debounceRef.current = null
        }, DEBOUNCE_MS)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchQuery, isOpen, runSearch])

    // Let touch scroll work inside the dropdown when it's inside a dialog (dialog scroll-lock would otherwise prevent touchmove)
    useEffect(() => {
        if (!isOpen) return
        let remove: (() => void) | null = null
        const id = requestAnimationFrame(() => {
            if (!listScrollRef.current) return
            const el = listScrollRef.current
            const handleTouchMove = (e: TouchEvent) => {
                if (el.contains(e.target as Node)) e.stopImmediatePropagation()
            }
            document.addEventListener('touchmove', handleTouchMove, { capture: true })
            remove = () => document.removeEventListener('touchmove', handleTouchMove, { capture: true })
        })
        return () => {
            cancelAnimationFrame(id)
            remove?.()
        }
    }, [isOpen])

    const handleSelect = (user: NonHomeownerUser) => {
        setValue(fullNamePath as any, user.fullname ?? "", { shouldValidate: true, shouldDirty: true })
        setValue(emailPath as any, user.email ?? "", { shouldValidate: true, shouldDirty: true })
        if (optionalPaths?.phone) setValue(optionalPaths.phone as any, user.phone ?? "", { shouldDirty: true })
        if (optionalPaths?.organizationName) {
            setOrganizationFromUserOrKeepDefault(setValue, optionalPaths.organizationName, user)
        }
        if (optionalPaths?.preferredLocation) setValue(optionalPaths.preferredLocation as any, getVolunteerPreferredLocation(user), { shouldDirty: true })
        if (optionalPaths?.dateOfBirth && user.dateOfBirth != null) {
            const dob = typeof user.dateOfBirth === "string" ? user.dateOfBirth : user.dateOfBirth
            setValue(optionalPaths.dateOfBirth as any, dob ?? "", { shouldDirty: true })
        }
        if (optionalPaths?.servePeriods) {
            const periods = mergeServePeriodsForGroupAddForm(
                user.volunteer?.serveDates,
                defaultServePeriod,
            )
            if (periods.length > 0) {
                setValue(optionalPaths.servePeriods as any, periods, { shouldDirty: true })
            }
        }
        if (optionalPaths?.profilePicture) {
            const url = (user as { profilePicture?: string | null }).profilePicture
            setValue(optionalPaths.profilePicture as any, url ?? undefined, { shouldDirty: true })
        }
        setSearchQuery("")
        setIsOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setValue(fullNamePath as any, "", { shouldValidate: true, shouldDirty: true })
        setValue(emailPath as any, "", { shouldValidate: true, shouldDirty: true })
        restoreGroupOrganizationField(setValue, optionalPaths?.organizationName, defaultGroupOrganization)
        if (optionalPaths?.profilePicture) {
            setValue(optionalPaths.profilePicture as any, undefined, { shouldDirty: true })
        }
        setSearchQuery("")
        setIsOpen(false)
    }

    const handleSelectNoEmailAvailable = () => {
        setValue(emailPath as any, NO_EMAIL_AVAILABLE_OPTION, { shouldValidate: true, shouldDirty: true })
        restoreGroupOrganizationField(setValue, optionalPaths?.organizationName, defaultGroupOrganization)
        if (optionalPaths?.profilePicture) {
            setValue(optionalPaths.profilePicture as any, undefined, { shouldDirty: true })
        }
        setSearchQuery("")
        setIsOpen(false)
    }

    const handleInputChange = (value: string) => {
        setSearchQuery(value)
        if (!isOpen) setIsOpen(true)
        if (!value.trim()) {
            setValue(emailPath as any, "", { shouldValidate: true, shouldDirty: true })
            if (optionalPaths?.profilePicture) {
                setValue(optionalPaths.profilePicture as any, undefined, { shouldDirty: true })
            }
            if (optionalPaths?.organizationName && !(fullName?.trim())) {
                restoreGroupOrganizationField(setValue, optionalPaths.organizationName, defaultGroupOrganization)
            }
        } else {
            setValue(emailPath as any, value, { shouldValidate: true, shouldDirty: true })
        }
    }

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open)
        if (!open) setSearchQuery("")
    }

    function getErrorByPath(path: string): string | undefined {
        const parts = path.split(".")
        let obj: unknown = errors
        for (const part of parts) {
            if (obj == null) return undefined
            obj = (obj as Record<string, unknown>)[part]
        }
        return obj && typeof obj === "object" && "message" in obj && typeof (obj as { message?: unknown }).message === "string"
            ? (obj as { message: string }).message
            : undefined
    }
    const emailError = getErrorByPath(emailPath)
    const errorMessage = emailError || inputGroupProps?.error

    return (
        <FormTextInputWrapper
            reserveMessageSlot
            inputGroupProps={{
                label,
                ...inputGroupProps,
                error: errorMessage,
                isDirty: !!email,
                highlightWhenEmpty: inputGroupProps?.highlightWhenEmpty ?? highlightWhenEmpty,
            }}
        >
            <div ref={containerRef} className="relative w-full">
                <Popover open={isOpen} onOpenChange={handleOpenChange}>
                    <PopoverTrigger asChild>
                        <div className="relative">
                            <Input
                                type="text"
                                placeholder={placeholder}
                                value={effectiveValue}
                                onChange={(e) => handleInputChange((e.target as HTMLInputElement).value)}
                                disabled={disabled}
                                variant={errorMessage ? "destructive" : "default"}
                                className={cn(
                                    "w-full pr-16",
                                    errorMessage && "border-destructive",
                                    showHighlight && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20"
                                )}
                                style={showHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : undefined}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                {displayValue && (
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        className="p-0.5 rounded hover:bg-muted"
                                        aria-label="Clear selection"
                                    >
                                        <X className="h-4 w-4 text-muted-foreground" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="p-0.5 rounded hover:bg-muted"
                                    onClick={() => setIsOpen(!isOpen)}
                                    disabled={disabled}
                                    aria-label="Toggle list"
                                >
                                    <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                                </button>
                            </div>
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[var(--radix-popover-trigger-width)] p-0"
                        align="start"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                        <div
                            ref={listScrollRef}
                            className="max-h-[280px] overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y"
                            style={{ WebkitOverflowScrolling: 'touch' }}
                            onWheel={(e) => e.stopPropagation()}
                        >
                            {isSearching ? (
                                <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Searching...
                                </div>
                            ) : (
                                <div className="p-1">
                                    <button
                                        type="button"
                                        onClick={handleSelectNoEmailAvailable}
                                        className={cn(
                                            "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                                            "hover:bg-accent hover:text-accent-foreground",
                                            email === NO_EMAIL_AVAILABLE_OPTION && "bg-accent"
                                        )}
                                    >
                                        <Check
                                            className={cn(
                                                "h-4 w-4 shrink-0",
                                                email === NO_EMAIL_AVAILABLE_OPTION ? "opacity-100 text-primary" : "opacity-0"
                                            )}
                                        />
                                        <span className="truncate">{NO_EMAIL_AVAILABLE_OPTION}</span>
                                    </button>
                                    {options.map((user) => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => handleSelect(user)}
                                            className={cn(
                                                "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                                                "hover:bg-accent hover:text-accent-foreground",
                                                email === user.email && "bg-accent"
                                            )}
                                        >
                                            <Check
                                                className={cn(
                                                    "h-4 w-4 shrink-0",
                                                    email === user.email ? "opacity-100 text-primary" : "opacity-0"
                                                )}
                                            />
                                            <span className="truncate">{getDisplayEmailValue(user.email?.trim() || "")}</span>
                                        </button>
                                    ))}
                                    {options.length === 0 && (
                                        <div className="px-2 py-3 text-center text-xs text-muted-foreground leading-snug">
                                            {searchQuery.trim()
                                                ? "No match—enter a new email."
                                                : "Type to search existing, or enter new."}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </FormTextInputWrapper>
    )
}
